// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
// eslint-disable-next-line spire/fenced-imports
import { Instance as TippyInstance } from "tippy.js";

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, AutocompleteModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import AutocompleteBrands, {
    AutocompleteBrandsStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteCategories";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";

import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Search from "@insite/mobius/Icons/Search";
import {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { TippyPopupStyle } from "@turnerBlueprint/App/ComponentExtensions/Tippy/TippyPopupExtensions";
import FancyScroll, { FancyScrollRefProps } from "@turnerBlueprint/App/Components/Common/FancyScroll";
import TippyPopup from "@turnerBlueprint/App/Components/Tippy/TippyPopup";
import { xHeaderUpdate } from "@turnerBlueprint/App/Handlers/XNG/Header";
import { CustomAppState } from "@turnerBlueprint/App/Store/Reducers";
import TextField, { TextFieldPresentationProps } from "@turnerBlueprint/MobiusOverrides/TextField/TextField";
import AutocompleteProducts, {
    AutocompleteProductsStyles,
} from "@turnerBlueprint/Overrides/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@turnerBlueprint/Overrides/Widgets/Header/SearchHistory";
import { HEADER_HEIGHT_CSS_VAR } from "@turnerBlueprint/XDL/constants";
import { getDataLayer, parseBoolean } from "@turnerBlueprint/XDL/Utilities";
import produce from "immer";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React, { createRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        // XNg-Changes
        mobileSearchOpen: state.xHeader.mobileSearchOpen,
        isSmallMobileScreen: state.xCommon.isSmallMobileScreen,
        isTabletScreen: state.xCommon.isTabletScreen,
        isMobileScreen: state.xCommon.isMobileScreen,
        shouldTrackSearch: state.xGA.searchEvent,
        shouldTrackAutocompleteSearch: state.xGA.autocompleteSearchEvent,
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
    // XNg-Changes
    xHeaderUpdate,
    loadRealTimePricing,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    // autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;

    // XNG-Changes
    autoCompleteModel?: AutocompleteModel & { productContexts: ProductContextModel[] };
    tippyInstance?: TippyInstance;
    isAutocompleteOpen?: boolean;
}

const DOWN_KEY = 40;
const UP_KEY = 38;
const ENTER_KEY = 13;

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    autocompleteColumnProductWrapper?: InjectableCss;
    autocompleteColumnProductMobileDivider?: InjectableCss;
    tippyPopup?: TippyPopupStyle;
    inputWrapper?: InjectableCss;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: { src: Search },
        border: "underline",
        cssOverrides: {
            formField: css`
                width: 250px;
            `,
        },
    },
    popover: {
        transitionDuration: "regular",
        toggle: false,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            // justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-direction: column-reverse;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 375px;
            padding: 0 20px 15px 20px;
        `,
    },
};

const styles = searchInputStyles;

// XNG-Changes
let realTimeRequestTimestamp: number;
class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    // XNG-Changes
    private inputWrapperRef = createRef<HTMLDivElement>();
    parentBoxRef = React.createRef<HTMLDivElement>();
    fancyScrollRef = React.createRef<FancyScrollRefProps>();

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
        const parsedQuery = parseQueryString<{ query?: string }>(this.props.location.search);

        this.state = {
            query: parsedQuery.query || "",
            focusableAutocompleteItems: [],
            isLoading: false,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            this.goToUrl(this.state.autoCompleteModel?.products[0].url);
        } else {
            const encodedQuery = encodeURIComponent(this.state.query);
            this.goToUrl(`/${this.props.searchPath}?query=${encodedQuery}`, false);
            
            if (this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // if we already on the Search page then scroll to top
                window.scrollTo(0, 0);
            }
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }
        // XNG-Changes
        if (this.props.shouldTrackSearch) {
            const dataLayer = getDataLayer();

            if (!dataLayer) {
                return;
            }

            const googleAnalyticsData = {
                event: "search",
                term: this.state.query,
                correctedterm: this.state.query,
                searchResults: this.state.autoCompleteModel?.products?.length,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };

    loadAutocomplete = debounce((query: string) => {
        this.props.getAutocompleteModel({
            query,
            onSuccess: result => {
                if (this.showAutocomplete) {
                    this.setState(
                        {
                            autoCompleteModel: {
                                ...result,
                                productContexts: (result?.products || []).map(product => ({
                                    product: {
                                        id: product.id!,
                                        productNumber: product.erpNumber,
                                        customerProductNumber: product.name,
                                        mediumImagePath: product.image,
                                        productTitle: product.title,
                                        canonicalUrl: product.url,
                                        uri: product.uri,
                                        canAddToCart: parseBoolean(product.properties.canAddToCart),
                                        manufacturerItem: product.manufacturerItemNumber,
                                        minimumOrderQty: parseInt(product.properties.minimumOrderQty || "1", 10) || 1,
                                        quoteRequired: parseBoolean(product.properties.isQuoteRequired),
                                        brand: {
                                            name: product.brandName,
                                            detailPagePath: product.brandDetailPagePath,
                                        },
                                        properties: {
                                            ...product.properties,
                                        } as any,
                                        unitOfMeasures: [
                                            {
                                                unitOfMeasure: product.properties.unitOfMeasure,
                                                unitOfMeasureDisplay: product.properties.unitOfMeasure,
                                            },
                                        ],
                                    } as ProductModel,
                                    productInfo: {
                                        productId: product.id!,
                                        productDetailPath: product.url,
                                        qtyOrdered: parseInt(product.properties.minimumOrderQty || "1", 10) || 1,
                                        unitOfMeasure: product.properties.unitOfMeasure,
                                    },
                                    onQtyOrderedChanged: (qty: number) => {
                                        const autoCompleteModel = produce(
                                            this.state.autoCompleteModel,
                                            draftedState => {
                                                if (!draftedState) {
                                                    return;
                                                }
                                                const productIndex = draftedState.productContexts.findIndex(
                                                    p => p.product.id === product.id,
                                                );
                                                draftedState.productContexts[productIndex].productInfo.qtyOrdered = qty;
                                            },
                                        );
                                        this.setState({ autoCompleteModel });
                                    },
                                })),
                            },
                        },
                        () => {
                            // XNG-Changes
                            this.loadRealtimePrices(this.state.autoCompleteModel as any);
                        },
                    );
                    // <XNG-Changes>
                    this.updatePopoverVisibility(result);

                    if (this.props.shouldTrackAutocompleteSearch) {
                        const dataLayer = getDataLayer();

                        if (!dataLayer) {
                            return;
                        }

                        const googleAnalyticsData = {
                            event: "autocompleteSearch",
                            term: query,
                            correctedterm: query,
                            searchResults: result?.products?.length,
                        };

                        dataLayer.push(googleAnalyticsData);
                    }
                    // </XNG-Changes>
                    // this.openAutocomplete();
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    loadRealtimePrices = (autocompleteResult: AutocompleteModel) => {
        if (autocompleteResult?.products?.length) {
            const currentRequestTimestamp = (realTimeRequestTimestamp = new Date().getTime());
            this.props.loadRealTimePricing({
                productPriceParameters: autocompleteResult?.products.map(product => ({
                    productId: product.id || "",
                    unitOfMeasure: product.properties.unitOfMeasure || "",
                    qtyOrdered: Number((product as any).minimumOrderQty) || 1,
                })),
                onSuccess: result => {
                    if (currentRequestTimestamp !== realTimeRequestTimestamp) {
                        return;
                    }
                    this.setState(current => {
                        result.realTimePricingResults?.forEach(pricing => {
                            const productTileData = current.autoCompleteModel?.productContexts?.find(
                                ({ productInfo }) => productInfo?.productId === pricing.productId,
                            );
                            if (productTileData && productTileData.productInfo) {
                                productTileData.productInfo.pricing = pricing;
                                delete productTileData.productInfo.failedToLoadPricing;
                            }
                        });
                        return {
                            autoCompleteModel: {
                                ...autocompleteResult,
                                productContexts: cloneDeep((autocompleteResult as any).productContexts),
                            },
                        };
                    });
                },
                onError: result => {
                    if (currentRequestTimestamp !== realTimeRequestTimestamp) {
                        return;
                    }
                    this.setState(current => {
                        current.autoCompleteModel?.productContexts?.every(({ productInfo }) => {
                            if (productInfo) {
                                productInfo.failedToLoadPricing = true;
                                delete productInfo.pricing;
                            }

                            return true;
                        });
                        return {
                            autoCompleteModel: {
                                ...autocompleteResult,
                                productContexts: cloneDeep((autocompleteResult as any).productContexts),
                            },
                        };
                    });
                },
            });
        }
    };

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        this.loadAutocomplete(newQuery);

        if (newQuery === "") {
            this.closeAutocomplete();
        } else {
            this.updatePopoverVisibility(this.state.autoCompleteModel);
        }
    };

    openAutocomplete = () => {
        // this.popover.current && (this.popover.current! as any).openPopover();
        // XNG-Changes
        this.state.tippyInstance?.show();
        this.setState({ isAutocompleteOpen: true });
    };

    closeAutocomplete = () => {
        // this.popover.current && (this.popover.current! as any).closePopover();
        // XNG-Changes
        this.state.tippyInstance?.hide();
        this.setState({ isAutocompleteOpen: false });
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.keyCode) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        this.props.history.push(url);
    };

    // <XNG-Changes>
    isPopoverVisible(results: AutocompleteModel | undefined) {
        let isVisible: boolean | null = false;
        if (this.state.query && this.state.query.length > 2 && results) {
            const { categories, brands, content, products } = results;
            isVisible =
                this.props.autocompleteEnabled &&
                ((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0) ||
                    (products && products.length > 0));
        } else if (!this.state.query) {
            const searchHistoryItems = getSearchHistory();
            isVisible = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        }
        return isVisible;
    }

    updatePopoverVisibility(results: AutocompleteModel | undefined) {
        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    handleInputFocus = () => {
        this.updatePopoverVisibility(this.state.autoCompleteModel);
        if (this.props.isTabletScreen && !this.props.mobileSearchOpen) {
            this.props.xHeaderUpdate({ key: "mobileSearchOpen", value: true });
        }
    };

    clearSearchHistoryCallback = () => {
        this.updatePopoverVisibility(undefined);
    };
    // </XNG-Changes>

    renderAutocomplete = () => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        // XNG-Changes
        if (this.fancyScrollRef.current) {
            this.fancyScrollRef.current.updateScrollable();
        }

        const { categories, brands, content, productContexts } = this.state.autoCompleteModel;
        const hasContent =
            (categories && categories.length > 0) || (brands && brands.length > 0) || (content && content.length > 0);
        return (
            <StyledWrapper
                {...this.styles.autocompleteWrapper}
                {...{
                    width: this.inputWrapperRef.current?.getBoundingClientRect().width || 300,
                    hasContent,
                }}
                data-test-selector="autocomplete-container"
                className="autocomplete-wrapper"
            >
                {((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0)) && (
                    <StyledWrapper {...this.styles.autocompleteColumnWrapper} className="autocomplete-column-wrapper">
                        <AutocompleteCategories
                            categories={categories}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteCategoriesStyles}
                        />
                        <AutocompleteBrands
                            brands={brands}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteBrandsStyles}
                        />
                        <AutocompleteContent
                            content={content}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteContentStyles}
                        />
                    </StyledWrapper>
                )}
                {productContexts &&
                    productContexts.length > 0 &&
                    (this.props.isTabletScreen ? (
                        <StyledWrapper
                            {...this.styles.autocompleteColumnProductWrapper}
                            className="autocomplete-column-wrapper"
                            {...{ hasContent }}
                            ref={this.parentBoxRef}
                        >
                            <AutocompleteProducts
                                products={productContexts}
                                focusedItem={this.state.focusedAutocompleteItem}
                                goToUrl={this.goToUrl}
                                extendedStyles={this.styles.autocompleteProductsStyles}
                                // XNG-Changes
                                {...{ doSearch: this.doSearch }}
                            />
                        </StyledWrapper>
                    ) : (
                        <>
                            <FancyScroll
                                refElement={this.parentBoxRef}
                                ref={this.fancyScrollRef}
                                isBoxShadow
                                shadowHeight="24px"
                            >
                                <StyledWrapper
                                    {...this.styles.autocompleteColumnProductWrapper}
                                    className="autocomplete-column-wrapper"
                                    {...{ hasContent }}
                                    ref={this.parentBoxRef}
                                >
                                    <AutocompleteProducts
                                        products={productContexts}
                                        focusedItem={this.state.focusedAutocompleteItem}
                                        goToUrl={this.goToUrl}
                                        extendedStyles={this.styles.autocompleteProductsStyles}
                                        // XNG-Changes
                                        {...{ doSearch: this.doSearch }}
                                    />
                                </StyledWrapper>
                            </FancyScroll>
                        </>
                    ))}

                {productContexts && productContexts.length > 0 && hasContent && (
                    <StyledWrapper {...this.styles.autocompleteColumnProductMobileDivider} />
                )}
            </StyledWrapper>
        );
    };

    // XNG-Changes
    renderSearchAutoCompleteContent = () => {
        const searchHistoryItems = getSearchHistory();
        const hasSearchHistory = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        if (!this.state.query && hasSearchHistory) {
            return (
                <SearchHistory
                    focusedQuery={this.state.focusedSearchHistoryQuery}
                    goToUrl={this.goToUrl}
                    extendedStyles={styles.searchHistoryStyles}
                    width={(this.inputWrapperRef.current?.getBoundingClientRect().width || 300) - 7}
                />
            );
        }
        if (this.state.query) {
            return this.renderAutocomplete();
        }
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}`;

        const { xHeaderUpdate, isMobileScreen } = this.props;
        const searchInput = (
            <StyledWrapper ref={this.inputWrapperRef} style={{ maxWidth: "881px", position: "relative" }}>
                <TextField
                    {...styles.input}
                    ref={this.props.inputRef}
                    isAnimated={this.props.isAnimated}
                    iconClickableProps={{ onClick: this.doSearch }}
                    value={this.state.query}
                    onChange={this.handleInputChange}
                    placeholder={translate("Search By Keyword")}
                    autoComplete="off"
                    clickableText={translate("submit search")}
                    id={searchId}
                    data-test-selector="headerSearchInputTextField"
                    className="search-input"
                    // XNG-Changes
                    onKeyDown={this.handlePopoverKeyDown}
                    aria-multiline="false"
                    aria-required="false"
                    aria-expanded={this.state.isAutocompleteOpen}
                    onFocus={this.handleInputFocus}
                    maxLength={100}
                    sizeVariant={!this.props.isSmallMobileScreen && this.props.isTabletScreen ? "small" : "default"}
                />
            </StyledWrapper>
        );

        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                {/* XNG-Changes: change popover with tippy */}
                <TippyPopup
                    onCreate={instance => {
                        this.setState({ tippyInstance: instance });
                    }}
                    onShow={instance => {
                        xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_search` });
                        this.setState({ tippyInstance: instance });
                    }}
                    onHidden={instance => {
                        this.setState({ isAutocompleteOpen: false });
                        xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_search` });
                    }}
                    content={<>{this.renderSearchAutoCompleteContent()}</>}
                    trigger="manual"
                    offset={[0, 0]}
                    duration={[null, null]}
                    arrow={false}
                    maxWidth="none"
                    placement="bottom-start"
                    type="menu"
                    mobileMode="left-side"
                    showCloseButton={false}
                    appendTo={() => document.body}
                    extendedStyles={styles.tippyPopup}
                    zIndex={1599}
                    popperOptions={{
                        strategy: "fixed",
                        modifiers: [
                            {
                                name: "preventOverflow",
                                options: {
                                    padding: 0,
                                },
                            },
                            {
                                name: "computeStyles",
                                options: {
                                    gpuAcceleration: false,
                                    adaptive: !isMobileScreen, // false for mobile only
                                    roundOffsets: ({ x, y }: any) => {
                                        const cssRoot = document?.querySelector(":root") as HTMLElement;
                                        const stickyHeader =
                                            cssRoot.style.getPropertyValue(HEADER_HEIGHT_CSS_VAR).replace("px", "") ||
                                            "168";
                                        return {
                                            x: isMobileScreen ? 0 : x,
                                            y: isMobileScreen ? stickyHeader : y,
                                        };
                                    },
                                },
                            },
                        ],
                    }}
                >
                    {searchInput}
                </TippyPopup>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
